import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import {
  OrderProduct,
  OrderProductOption,
  OrderProductService,
  OrderProductStatus,
  OrderReturnStatus,
  OrderType,
} from '../../../../clients/quipli-service/order/order';
import {
  OrderInputItem,
  OrderInputRental,
  isOrderCustomInput,
} from '../../../../clients/quipli-service/order/order-calculation';
import {
  ProductLineItem,
  CustomProductLineItem,
  ProductFeeLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { CustomOrderPriceRequest, RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { ShippingType } from '../../../../clients/quipli-service/delivery/delivery';
import { ProductTrackingType } from '../../../../clients/quipli-service/product/product';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { useTaxApiV1 } from '../../../../clients/quipli-service/tax/tax-api-v1';
import { convertToMoney } from '../../../../util/util';
import { Currency } from 'dinero.js';
import { ItemType } from '../../../../clients/quipli-service/accounting/accounting';
import { Location } from '../../../../clients/quipli-service/location/location';

export interface ProductItemStateFee extends ProductFeeLineItem {
  title: string;
  canEdit: boolean;
}

export interface ProductItemStateRentalRates {
  current: CustomOrderPriceRequest[];
  original: CustomOrderPriceRequest[];
}

export function getCanEditUnitPrice({
  isTaxInclusive,
  isOrderCycleBilled,
  isRental,
  inputItem,
}: {
  isTaxInclusive: boolean;
  isOrderCycleBilled: boolean;
  isRental: boolean;
  inputItem?: OrderInputItem;
}) {
  // Base condition: must not be tax inclusive
  if (isTaxInclusive) {
    return false;
  }

  // If it's a custom input, only non-rental products can be edited
  if (inputItem && isOrderCustomInput(inputItem)) {
    return !isRental;
  }

  // If order is cycle billed, only Sale products can be edited
  if (isOrderCycleBilled) {
    return !isRental; // Sale products are !isRental
  }

  // If not cycle both Rental and Sale products can be edited
  return true;
}

function extractDatesAndUnits({
  isRental,
  orderProduct,
  inputItem,
}: {
  isRental: boolean;
  orderProduct?: OrderProduct;
  inputItem?: OrderInputItem;
}) {
  const startDate = ((inputItem as OrderInputRental | undefined)?.start || orderProduct?.rentalStartDate)!;

  let units: number[] = [];
  if (inputItem && !isOrderCustomInput(inputItem)) {
    units = inputItem.assignedUnits || [];
  }

  if (!units.length && orderProduct?.assignedUnits) {
    units = orderProduct.assignedUnits.map((unit) => unit.id);
  }

  if (!isRental) {
    return {
      units,
      startDate,
      endDate: undefined,
      customRentalRates: {
        current: [],
        original: [],
      },
    };
  }

  const endDate = (inputItem as OrderInputRental | undefined)?.end || orderProduct?.rentalEndDate;
  const originalCustomOrderRentalRates = orderProduct?.customOrderRentalRates;
  const customOrderRentalRates =
    (inputItem as OrderInputRental | undefined)?.customOrderRentalRates || orderProduct?.customOrderRentalRates;

  return {
    units,
    startDate,
    endDate,
    customRentalRates: {
      current: customOrderRentalRates || [],
      original: originalCustomOrderRentalRates || [],
    },
  };
}

function extractPricingData(
  isNewProduct: boolean,
  isRental: boolean,
  orderProduct?: OrderProduct,
  inputItem?: OrderInputItem,
  financialItem?: ProductLineItem | CustomProductLineItem,
  isTaxInclusive?: boolean,
  isOrderCycleBilled?: boolean,
  financialFees?: ProductFeeLineItem[],
) {
  const quantity = inputItem?.quantity || orderProduct?.quantity || 1;

  // Discount
  const originalDiscount = orderProduct?.discountPercentage || 0;
  const discount = inputItem?.manualDiscount !== undefined ? inputItem.manualDiscount : originalDiscount;

  // Price
  const originalUnitPrice = isNewProduct
    ? financialItem?.hasCustomAmount
      ? -1
      : financialItem?.unitPrice.toUnit() || 0
    : orderProduct?.customerBuyingPrice || 0;

  const unitPrice =
    financialItem?.unitPrice.toUnit() ||
    (inputItem?.customPrice !== undefined ? inputItem.customPrice / 100 : originalUnitPrice);

  const total = unitPrice * quantity * (discount ? 1 - discount / 100 : 1);

  // Process fees
  const fees: ProductItemStateFee[] = (financialFees || []).map((fee) => ({
    ...fee,
    title: fee.description + (fee.taxable ? ' - (Taxable)' : ''),
    canEdit: !isTaxInclusive && !isOrderCycleBilled,
  }));

  return {
    quantity,
    unitPrice: {
      current: unitPrice,
      original: originalUnitPrice,
      canEdit: getCanEditUnitPrice({
        isTaxInclusive: isTaxInclusive || false,
        isOrderCycleBilled: isOrderCycleBilled || false,
        isRental,
        inputItem,
      }),
      canReset: unitPrice !== originalUnitPrice && (!inputItem || !isOrderCustomInput(inputItem)),
    },
    discount: {
      current: discount,
      original: originalDiscount,
      canReset: discount !== originalDiscount,
    },
    total,
    fees,
    isTaxInclusive: isTaxInclusive || false,
  };
}

function extractProductName(orderProduct?: OrderProduct, financialItem?: ProductLineItem | CustomProductLineItem) {
  if (orderProduct?.product.type === 'Custom') {
    return `${orderProduct.name}${orderProduct.description ? `: ${orderProduct.description}` : ''}`;
  }

  if (orderProduct?.product?.sku) {
    return `${orderProduct.product.name} (${orderProduct.product.sku})`;
  }

  return orderProduct?.product?.name || financialItem?.description || 'Unknown Product';
}

export interface ProductItemState {
  // Mode
  isLoading: boolean;
  isEditing: boolean;

  // Core identification
  id: string;
  productId: number;
  type: OrderType;

  // Product info
  name: string;
  sku?: string;
  trackingType: ProductTrackingType;
  isCustomProduct: boolean;
  isNewProduct: boolean;
  units: number[];
  options: OrderProductOption[];
  services: OrderProductService[];
  equipmentOwner?: string;
  status: OrderProductStatus;
  returnStatus: OrderReturnStatus;
  initialStock: {
    rentalStock?: number;
    saleStock?: number;
  };

  // Pricing
  isTaxInclusive: boolean;
  totalTaxPercentage: number;
  taxedUnitPrice: number;
  fees: ProductItemStateFee[];
  quantity: number;
  unitPrice: {
    current: number;
    original: number;
    canReset: boolean;
    canEdit: boolean;
  };
  discount: {
    current: number;
    original: number;
    canReset: boolean;
  };
  total: number;

  // Rental specific
  isRental: boolean;
  startDate?: DateTime;
  endDate?: DateTime;
  rentalPrices: RentalProductPrice[];
  customRentalRates: ProductItemStateRentalRates;

  // Delivery
  delivery: {
    type?: ShippingType;
    description?: { productId: number; text: string };
  };

  // Status
  canEdit: boolean;
}

export interface ProductItemDataReturn extends ProductItemState {
  // Tax setters for use by other hooks
  setTaxedUnitPrice: (price: number) => void;
  setTotalTaxPercentage: (percentage: number) => void;
  // Additional data for new products
  fetchedProduct?: any;
  updateInputItem: (updates: Partial<OrderInputItem>) => void;
}

export const useProductItemData = ({
  isEditing,
  isTaxInclusive,
  isOrderCycleBilled,
  orderProduct,
  inputItem,
  financialItem,
  financialFees,
  rentalPrices,
  deliveryDescription,
  // Tax calculation parameters
  taxCodeId,
  location,
  onChange,
}: {
  isEditing: boolean;
  isTaxInclusive: boolean;
  isOrderCycleBilled: boolean;
  orderProduct?: OrderProduct;
  inputItem?: OrderInputItem;
  financialItem?: ProductLineItem | CustomProductLineItem;
  financialFees: ProductFeeLineItem[];
  rentalPrices?: RentalProductPrice[];
  deliveryDescription?: { productId: number; text: string };
  // Tax calculation parameters
  taxCodeId?: string;
  location: Location;
  onChange: (orderInputItem: OrderInputItem) => void;
}): ProductItemDataReturn => {
  const { getProduct } = useAPI();
  const { getItemTax } = useTaxApiV1();

  // State for tax calculations
  const [taxedUnitPrice, setTaxedUnitPrice] = useState<number | undefined>(undefined);
  const [totalTaxPercentage, setTotalTaxPercentage] = useState<number | undefined>(undefined);

  // State for product fetching
  const [isLoadingProduct, setIsLoadingProduct] = useState<boolean>(false);
  const [fetchedProduct, setFetchedProduct] = useState<any>(undefined);

  // Use ref to always get the latest inputItem
  const inputItemRef = useRef(inputItem);
  inputItemRef.current = inputItem;

  // Generic handler that updates any property of the inputItem
  const updateInputItem = useCallback(
    (updates: Partial<OrderInputItem>) => {
      if (!inputItemRef.current || !onChange) {
        return;
      }

      onChange({ ...inputItemRef.current, ...updates } as OrderInputItem);
    },
    [onChange],
  );

  // Tax calculation effect
  useEffect(() => {
    if (!taxCodeId || (!inputItem?.productId && !orderProduct?.id)) {
      return;
    }

    const type = (inputItem?.type || orderProduct?.type)!;
    const isNewProduct = !!inputItem && !orderProduct;
    const itemId = (isNewProduct ? inputItem?.productId.toString() : orderProduct?.id.toString())!;

    // Get unit price from the pricing data
    const originalUnitPrice = isNewProduct
      ? financialItem?.hasCustomAmount
        ? -1
        : financialItem?.unitPrice.toUnit() || 0
      : orderProduct?.customerBuyingPrice || 0;

    const unitPrice =
      financialItem?.unitPrice.toUnit() ||
      (inputItem?.customPrice !== undefined ? inputItem.customPrice / 100 : originalUnitPrice);

    const taxable = orderProduct?.isTaxFree !== true ? true : financialItem?.taxable ?? true;

    getItemTax({
      id: taxCodeId,
      itemPrice: convertToMoney(unitPrice, location.currency as Currency),
      itemId,
      transactionType: type,
      taxable,
      itemType: ItemType.Product,
    }).then((tax: any) => {
      if (tax) {
        setTaxedUnitPrice(unitPrice * (1 + tax.totalPercentage / 100));
        setTotalTaxPercentage(tax.totalPercentage);
      }
    });
  }, [
    inputItem?.productId,
    inputItem?.customPrice,
    orderProduct?.id,
    orderProduct?.isTaxFree,
    financialItem?.taxable,
    financialItem?.unitPrice,
    financialItem?.hasCustomAmount,
  ]);

  // Product fetching effect for new products
  // Trying to understan why this isn not required and why it broke the app
  useEffect(() => {
    if (!inputItem || orderProduct || isOrderCustomInput(inputItem)) {
      return;
    }

    const isRental = inputItem.type === OrderType.Rental;
    const isNewProduct = !!inputItem && !orderProduct;

    if (isNewProduct && isRental && location?.id) {
      setIsLoadingProduct(true);
      getProduct(inputItem.productId)
        .then(async (product) => {
          setFetchedProduct(product);
          setIsLoadingProduct(false);
        })
        .catch(() => {
          setIsLoadingProduct(false);
        });
    }
  }, [inputItem?.id, orderProduct?.id]);

  return useMemo(() => {
    const isRental = (inputItem?.type || orderProduct?.type) === OrderType.Rental;
    const isNewProduct = !!inputItem && !orderProduct;

    // Extract core identification data
    const id = isNewProduct ? inputItem.id : `${orderProduct?.id}`;
    const productId = inputItem?.productId || orderProduct?.product.id || 0;
    const type = inputItem?.type || orderProduct?.type || OrderType.Sale;

    // Extract product info
    const name = extractProductName(orderProduct, financialItem);
    const sku = orderProduct?.product?.sku;
    const trackingType =
      orderProduct?.product.trackingType || fetchedProduct?.trackingType || ProductTrackingType.NonInventory;
    const options = orderProduct?.options || [];
    const services = orderProduct?.services || [];
    const equipmentOwner = orderProduct?.equipmentOwner;

    // Extract rental data
    const rentalData = extractDatesAndUnits({ isRental, orderProduct, inputItem });

    // Extract pricing data
    const pricingData = extractPricingData(
      isNewProduct,
      isRental,
      orderProduct,
      inputItem,
      financialItem,
      isTaxInclusive,
      isOrderCycleBilled,
      financialFees,
    );

    // Extract delivery info
    const delivery = {
      type: orderProduct?.shippingType,
      description: deliveryDescription,
    };

    // Determine edit capabilities
    const canEdit = !inputItem || !isOrderCustomInput(inputItem);

    // Extract initial stock
    const locationStock = orderProduct?.product.stock?.find((s) => s.locationId === location.id);
    const initialStock = {
      rentalStock: locationStock?.rentalStock || orderProduct?.product.rentalStock,
      saleStock: locationStock?.saleStock || orderProduct?.product.saleStock,
    };

    return {
      // Mode
      isLoading: !inputItem || isLoadingProduct,
      isEditing,

      // Core identification
      id,
      productId,
      type,

      // Product info
      name,
      sku,
      trackingType,
      isCustomProduct: orderProduct?.product.type === 'Custom' || (inputItem ? isOrderCustomInput(inputItem) : false),
      isNewProduct,
      units: rentalData.units,
      initialStock,
      options,
      services,
      equipmentOwner,
      // FIXME: the names are swapped from the server
      status: orderProduct?.productStatus || OrderProductStatus.Pending,
      returnStatus: orderProduct?.status || OrderReturnStatus.NotStarted,

      // Pricing
      ...pricingData,
      // Override tax values with calculated state
      totalTaxPercentage: totalTaxPercentage || 0,
      taxedUnitPrice: taxedUnitPrice || pricingData.unitPrice.current,
      unitPrice: {
        ...pricingData.unitPrice,
        canReset: pricingData.unitPrice.current !== pricingData.unitPrice.original,
      },
      discount: {
        ...pricingData.discount,
        canReset: pricingData.discount.current !== pricingData.discount.original,
      },

      // Rental specific
      isRental,
      startDate: rentalData.startDate,
      endDate: rentalData.endDate,
      rentalPrices: isRental ? rentalPrices || [] : [],
      customRentalRates: rentalData.customRentalRates,

      // Delivery
      delivery,

      // Status
      canEdit,

      // Tax setters for use by other hooks
      setTaxedUnitPrice,
      setTotalTaxPercentage,

      // Generic handler that updates any property of the inputItem
      updateInputItem,

      // Additional data for new products
      fetchedProduct,
    };
  }, [
    orderProduct,
    inputItem,
    financialItem,
    financialFees,
    rentalPrices,
    deliveryDescription,
    isEditing,
    isTaxInclusive,
    isOrderCycleBilled,
    totalTaxPercentage,
    taxedUnitPrice,
    isLoadingProduct,
    fetchedProduct,
  ]);
};
